import * as Hooks from 'preact/hooks';
import { PropertySelections, PropertyOptions, PropertyValue, ResolveSelection } from './types';
import { resolveChoice } from './compatibility';
import { ResolveFurnitureChoiceTree, resolveFurnitureChoiceTree } from './furniture';
import { getBasketForParticles } from './basket';
import { useCachingAsyncCallback } from './useCachingAsyncCallback';
import * as Selectors from '../../store/selectors';
import { useSelector } from '../hooks/redux';
import { AnyAction, Dispatch } from 'redux';

export function useCachingSelectionResolver(
    spaceId: string | undefined,
    currentSelections: PropertySelections | undefined,
    compatiblePropertyOptions: PropertyOptions | undefined,
    fixedSelections: PropertySelections | undefined,
    fallbacks?: PropertySelections[],
): ResolveSelection | undefined {
    const gavelUrl = useSelector(Selectors.Core.configurationServiceUrl);
    const tenantId = useSelector(Selectors.Core.tenantIdentifier);
    const resolveSelectionsCached = useCachingAsyncCallback(
        async (property, value, compatiblePropertyOptions, currentSelections) =>
            spaceId && gavelUrl && tenantId
                ? await resolveChoice(
                      gavelUrl,
                      tenantId,
                      spaceId,
                      property,
                      value,
                      compatiblePropertyOptions,
                      currentSelections,
                      fixedSelections,
                      fallbacks,
                  )
                : {},
        [gavelUrl, tenantId, spaceId, fixedSelections, fallbacks],
    );

    const resolveSelectionsFinal = Hooks.useCallback(
        async (property: string, value: PropertyValue) =>
            compatiblePropertyOptions && currentSelections
                ? await resolveSelectionsCached(
                      property,
                      value,
                      compatiblePropertyOptions,
                      currentSelections,
                  )
                : {},
        [resolveSelectionsCached, compatiblePropertyOptions, currentSelections],
    );

    return spaceId && compatiblePropertyOptions && currentSelections
        ? resolveSelectionsFinal
        : undefined;
}

export function useCachingResolveFurnitureChoiceTree(
    spaceId: string | undefined,
    modelsUrl: string | undefined,
    dispatch: Dispatch<AnyAction>,
    disableErrorHandling: boolean,
): {
    resolveFurnitureChoiceTree: ResolveFurnitureChoiceTree;
} {
    const gavelUrl = useSelector(Selectors.Core.configurationServiceUrl);
    const tenantId = useSelector(Selectors.Core.tenantIdentifier);
    return {
        resolveFurnitureChoiceTree: useCachingAsyncCallback(
            async (selections: PropertySelections) => {
                if (!gavelUrl || !tenantId || !spaceId || !modelsUrl) return null;
                return await resolveFurnitureChoiceTree(
                    gavelUrl,
                    tenantId,
                    spaceId,
                    selections,
                    modelsUrl,
                    dispatch,
                    disableErrorHandling,
                );
            },
            [gavelUrl, tenantId, spaceId, modelsUrl],
        ),
    };
}
export function useCachingGetBasketforParticles(
    dispatch: Dispatch<AnyAction>,
    disableErrorHandling: boolean,
) {
    const gluonUrl = useSelector(Selectors.Core.particleMappingServiceUrl);
    const tenantId = useSelector(Selectors.Core.tenantIdentifier);
    const cached = useCachingAsyncCallback(
        async particles => {
            if (!gluonUrl) {
                throw new Error('attempted to get basket before particle mapping url set');
            }
            if (!tenantId) {
                throw new Error('attempted to get basket before tenant id set');
            }
            return await getBasketForParticles(
                gluonUrl,
                tenantId,
                particles,
                dispatch,
                disableErrorHandling,
            );
        },
        [gluonUrl, tenantId],
    );

    const stable = Hooks.useCallback(
        (furnitureParticles: Array<string>) => cached(furnitureParticles.slice().sort()),
        [cached],
    );
    return {
        getBasketForParticles: stable,
    };
}
